import { formatDate } from "../utils/date";
import { createTemporaryId, isCreated } from "../utils/model";
import { isValidPhoneNumber, PhoneNumberInfoOptions } from "sweet-validator-utils";
import { TextInputProps } from "../components/core/field/TextInput";
import { JsonSchema } from "./JsonSchema";
import { boolean } from "boolean";
import { text } from "../utils/i18n";
import { PredefinedValue } from "./PredefinedValue";

/**
 * Represents a base field, which is created by a user and allows
 * for flexible entity information.
 */
export default class BasicField {
  readonly id: string = createTemporaryId();
  readonly key: string = createTemporaryId();
  readonly label: string;
  readonly type: FieldType = FieldType.Plain;
  readonly isRemoved: boolean = false;
  readonly isRemovable: boolean = true;
  readonly predefinedValues: PredefinedValue[] = [];
  readonly description?: string;
  readonly json?: JsonSchema;
  /**
   * Null means any project or none.
   */
  readonly projectIds?: string[] = null;

  constructor(deriveFrom?: Partial<BasicField>) {
    //  Previous fields may have a flag `removed` instead of `isRemoved`.
    if ((deriveFrom as any)?.removed === true) {
      deriveFrom = {
        ...deriveFrom,
        isRemoved: true
      };
    }

    if (deriveFrom) {
      Object.assign(this, deriveFrom);
    }

    if (this.predefinedValues) {
      this.predefinedValues = this.predefinedValues.map(predefinedValue => new PredefinedValue(predefinedValue));
    }

    if (this.json) {
      this.json = new JsonSchema(this.json);
    }
  }

  get isCreated() {
    return isCreated(this.id);
  }

  /**
   * @returns `str` formatted according to the field type.
   */
  formatString(str: string, opts = {
    forceDateTime: false
  }) {
    if (!str) {
      return null;
    }

    switch (this.type) {
      case FieldType.Date:
        return formatDate(typeof str === "string" ? new Date(str) : str, {
          time: opts.forceDateTime !== false,
          year: 'include'
        });
    }

    return str;
  }
}

export const defaultDateFormat = 'YYYY-MM-DD';

/**
 * Note: a field's type can be used for **basic autofilling** but consider the following issues with relying on the field's type (from a previous change):
 *  - two fields can have the same type and thus incorrect information can be set (e.g. contact address, shipping address),
 *  - fields are likely configured once by us anyway and it's better to fine-tune each integration.
 */
export enum FieldType {
  Plain = 'plain',
  Date = 'date',
  Numeric = 'numeric',

  /**
   * Set automatically after BankID sign.
   */
  PersonIdentityNumber = 'personIdentityNumber',

  /**
   * Used for autofilling offers' "recipient.name".
   */
  FirstName = 'firstName',

  /**
   * Used for autofilling offers' "recipient.name".
   */
  LastName = 'lastName',

  /**
   * Used for autofilling offers' "recipient.name".
   */
  MiddleName = 'middleName',

  /**
   * Used for autofilling offers' "recipient.name".
   */
  FullName = 'fullName',

  /**
   * Used for autofilling offers' "recipient.sms".
   */
  PhoneNumber = 'phoneNumber',

  /**
   * Used for autofilling offers' "recipient.email".
   */
  Email = 'email',

  /**
   * Should default to an email address but is not intended to
   * automatically set an agreement's recipient email, etc.
   * 
   * If the field should not be auto-filled then it should be of type Plain.
   */
  SecondaryEmail = 'secondaryEmail',

  /**
   * Used for web forms.
   */
  Boolean = 'boolean',
}

export const simpleFieldTypes: FieldType[] = [FieldType.Plain, FieldType.Date, FieldType.Numeric, FieldType.PhoneNumber];
export const advancedFieldTypes: FieldType[] = Object.values(FieldType).filter(type => !simpleFieldTypes.includes(type));

export function translateFieldType(type: FieldType): string {
  const translations: { [type in FieldType]: string } = {
    [FieldType.Plain]: text`Vanlig text`,
    [FieldType.Numeric]: text`Numerisk`,
    [FieldType.Date]: text`Datum`,
    [FieldType.FirstName]: text`Förnamn`,
    [FieldType.LastName]: text`Efternamn`,
    [FieldType.MiddleName]: text`Mellannamn`,
    [FieldType.PersonIdentityNumber]: text`Personnummer`,
    [FieldType.FullName]: text`Fullständigt namn`,
    [FieldType.PhoneNumber]: text`Telefonnummer`,
    [FieldType.Email]: text`Epost`,
    [FieldType.SecondaryEmail]: text`Sekundär epost`,
    [FieldType.Boolean]: text`Ja / nej`,
  };

  return translations[type] || type;
}

/**
 * Returns a list of translated features for an advanced field type.
 */
export function listFieldTypeFeatures(type: FieldType): string[] {
  const features: { [type in FieldType]: string[] } = {
    [FieldType.Plain]: [],
    [FieldType.Numeric]: [],
    [FieldType.Date]: [],
    [FieldType.FirstName]: [],
    [FieldType.LastName]: [],
    [FieldType.MiddleName]: [],
    [FieldType.PersonIdentityNumber]: [],
    [FieldType.Boolean]: [],
    [FieldType.FullName]: [text`Fyller automatiskt i mottagares namn på avtal`],
    [FieldType.PhoneNumber]: [text`Fyller automatiskt i mottagares telefonnummer på avtal`],
    [FieldType.Email]: [text`Fyller automatiskt i mottagares epost på avtal`],
    [FieldType.SecondaryEmail]: [text`Fylls i automatiskt med epost`],
  };

  return features[type] || [];
}

export function getFieldTypeIcon(type: FieldType): string | null {
  const translations = {
    [FieldType.FirstName]: "user",
    [FieldType.LastName]: "user",
    [FieldType.MiddleName]: "user",
    [FieldType.PersonIdentityNumber]: "user",
    [FieldType.FullName]: "user",
    [FieldType.PhoneNumber]: "phone",
    [FieldType.Email]: "envelope",
    [FieldType.SecondaryEmail]: "envelope",
  };

  return translations[type] || null;
}

/**
 * Returns props for a text input, given a field type.
 */
export function getInputPropsByFieldType(fieldType: FieldType): Pick<TextInputProps, 'allow' | 'inputMode'> {
  if (fieldType === FieldType.Numeric) {
    return {
      allow: "numericSpecial",
      inputMode: "decimal",
    }
  }

  return {
    allow: "all",
    inputMode: "text",
  }
}

export function getBooleanValue(value: string): boolean {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  const lc = value.trim().toLowerCase();
  return lc === 'ja' || boolean(value);
}

/**
 * References a `BasicField` and contains a value.
 */
export class BasicFieldReference {

  /**
   * @returns A subset of `references` whose value is a first name, last name, or both.
   */
  static filterName(references: BasicFieldReference[]) {
    return references.filter(reference => (
      /^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð ,.'-]+$/.test(reference.value)
    ));
  }

  readonly fieldId: string;
  readonly value: string;
  /**
   * Whether the value was changed by the user. This is relevant for assistants.
   */
  readonly changedByUser?: boolean = false;
  readonly predefinedValues?: PredefinedValue[];

  constructor(deriveFrom?: Partial<BasicFieldReference>) {
    if (deriveFrom) {
      Object.assign(this, deriveFrom);
    }
    
    this.predefinedValues = this.predefinedValues?.map(element => new PredefinedValue(element));

    if (typeof this.value !== "string") {
      this.value = "";
    }
  }
}

/**
 * Returns a list of field references.
 * Each reference in `list` is replaced with corresponding reference in `update`, but only
 * if the new reference has a non-empty value.
 * @param allowRemoval Whether to allow replacing a field reference with an empty value if it exists in `update`. 
 */
export function mergeFieldReferences(list: BasicFieldReference[], update: BasicFieldReference[], allowRemoval: boolean = false) {
  if (!allowRemoval) {
    update = update.filter(({ value }) => value);
  }

  const valueByFieldId = [...list, ...update]
    .reduce(
      (object, { fieldId, value }) => {
        object[fieldId] = value;
        return object;
      }, {} as { [fieldId: string]: string }
    );

  return Object.keys(valueByFieldId).map(fieldId => ({
    fieldId,
    value: valueByFieldId[fieldId]
  }));
}